import { Command } from "clipanion";
import type { CompanionBridgeEvent } from "@m8t-stack/companion-bridge-contract";
import { M8tCommand } from "../../lib/m8t-command.js";
import {
  statusCompanion,
  type CompanionInstallState,
} from "../../lib/companion-install.js";
import {
  checkCompanionUpdate,
  companionUpdateAvailable,
} from "../../lib/companion-update-check.js";
import { platformTag } from "@m8t-stack/platform-release";
import { readCompanionRelease, type CompanionRelease } from "../../lib/companion-channel.js";
import { companionAssetFor } from "../../lib/companion-download.js";
import { defaultLocalCompanionInstallOptions } from "./install.js";

interface WritableLike {
  write(chunk: string): unknown;
}

type UpdateAnswer = Extract<CompanionBridgeEvent, { type: "update" }>;

/** Rejects if `work` outruns `ms`, so a hung socket reads as "no answer". */
async function withTimeout<T>(work: Promise<T>, ms: number): Promise<T> {
  let timer: NodeJS.Timeout | undefined;
  try {
    return await Promise.race([
      work,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => { reject(new Error("channel timed out")); }, ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export interface CompanionStatusOptions {
  readonly update?: () => Promise<UpdateAnswer>;
  readonly channel?: () => Promise<CompanionRelease | null>;
  readonly timeoutMs?: number;
  /**
   * Which machine to answer FOR. Named rather than read, so the host is a fact
   * a test can state — reading `process` here made the answer depend on where
   * the suite happened to run, which is how a green macOS run became a red
   * Linux one.
   */
  readonly host?: { readonly platform: NodeJS.Platform; readonly architecture: string };
}

export async function runCompanionStatusCommand(
  stdout: WritableLike,
  status: () => Promise<CompanionInstallState>,
  options: CompanionStatusOptions = {},
): Promise<number> {
  const { update, channel, timeoutMs = 10_000, host } = options;
  const state = await status();
  if (state.state === "not-installed") {
    stdout.write("Desktop companions are not installed.\n");
    // "Not installed" is the one thing the person running this already knows.
    // What they cannot tell apart on their own is a release that carries no
    // companions from an install that should have put them here and didn't —
    // and that ambiguity is why the first occurrence of the latter went
    // undiagnosed. Best-effort and last: an unreachable channel leaves the
    // answer above exactly as it was, because a machine that is offline has
    // not told us anything about this install.
    //
    // `null` is an ANSWER (this release carries none); a throw is NO answer at
    // all. Collapsing the two would let an offline machine be told its release
    // carries no companions — a claim nothing verified, which is the same
    // defect this feature exists to remove, pointed the other way.
    let answered: { readonly release: CompanionRelease | null } | null = null;
    if (channel) {
      // Bounded, because this command used to answer instantly from local state
      // alone: a channel that hangs rather than refusing must not turn `status`
      // into something that never returns.
      try { answered = { release: await withTimeout(channel(), timeoutMs) }; } catch { answered = null; }
    }
    if (answered === null) return 0;
    const { release } = answered;
    if (release === null) {
      stdout.write(
        "The latest platform release carries no desktop companions.\n" +
        "They arrive with a platform release — run: m8t platform update\n",
      );
    } else if (companionAssetFor(release.component, host?.platform, host?.architecture) === null) {
      // Reachable on a supported OS whose ARCH the release simply did not ship
      // — so it names the gap rather than repeating the OS rule, which would be
      // both unhelpful and, on (say) a Windows machine, plainly wrong.
      stdout.write(
        `The latest platform release (${platformTag(release.platformVersion)}) carries no companion build for this host.\n`,
      );
    } else {
      stdout.write(
        `A companion build is available (${release.component.version}).\nRun: m8t companion install\n`,
      );
    }
    return 0;
  }
  if (state.state === "needs-repair") {
    stdout.write(`Desktop companions need repair: ${state.reason}\n`);
    return 1;
  }
  stdout.write(
    `Desktop companions are installed (${state.version}); start at login: ${state.startAtLogin ? "on" : "off"}.\n`,
  );
  // Best-effort and last: what is installed has already been reported, and an
  // unreachable channel must not turn a healthy install into a failure.
  if (update) {
    const answer = await update().catch(() => null);
    if (answer !== null && companionUpdateAvailable(answer)) {
      stdout.write(
        `A newer companion is available (${String(answer.available)}).\nRun: m8t companion install\n`,
      );
    }
  }
  return 0;
}

export class CompanionStatusCommand extends M8tCommand {
  static paths = [["companion", "status"]];
  static usage = Command.Usage({
    description: "Verify the installed desktop companion without launching it.",
  });

  async executeCommand(): Promise<number> {
    const options = defaultLocalCompanionInstallOptions();
    return runCompanionStatusCommand(this.context.stdout, () => statusCompanion(options), {
      update: () => checkCompanionUpdate(options),
      channel: () => readCompanionRelease(),
      host: { platform: options.platform, architecture: options.architecture },
    });
  }
}
